import React, { useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  Chip,
  Typography,
  Skeleton,
} from "@mui/material";
import type { Offer } from "../../lib/types/detailsTypes";
import OfferDetailsDialog from "./offerDetailsDialog";

interface OfferCardProps {
  offer: Offer;
  /** Render the card without a dialog (e.g. inside a different modal) */
  disableDialog?: boolean;
}

const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  disableDialog = false,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const remaining =
    offer.usageLimit !== undefined
      ? offer.usageLimit - offer.redeemedCount
      : null;

  const showLowStock = remaining !== null && remaining < 5 && remaining > 0;

  const handleOpenDialog = () => {
    if (!disableDialog) setDialogOpen(true);
  };

  return (
    <>
      <Card
        elevation={4}
        onClick={handleOpenDialog}
        sx={{
          width: "100%",
          maxWidth: 400,
          borderRadius: 2,
          overflow: "hidden",
          my: 2,
          cursor: disableDialog ? "default" : "pointer",
        }}
      >
        <Box sx={{ display: "flex", p: 2, alignItems: "center" }}>
          {/* image block */}
          <Box sx={{ width: 100, height: 100, mr: 2, position: "relative" }}>
            {!imageLoaded && (
              <Skeleton
                variant="rectangular"
                width={100}
                height={100}
                sx={{ borderRadius: 2, position: "absolute", inset: 0 }}
              />
            )}
            <CardMedia
              component="img"
              image={offer.imageUrl || "/default-store-logo.png"}
              alt={offer.title}
              sx={{
                width: 100,
                height: 100,
                borderRadius: 2,
                objectFit: "cover",
                display: imageLoaded ? "block" : "none",
              }}
              onLoad={() => setImageLoaded(true)}
            />
          </Box>

          {/* text block */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
              <strong>{offer.title}</strong>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {offer.description}
            </Typography>

            {showLowStock && (
              <Chip
                label={`Only ${remaining} remaining`}
                size="small"
                color="warning"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Box>
      </Card>

      {/* Dialog lives here */}
      {!disableDialog && (
        <OfferDetailsDialog
          open={dialogOpen}
          offer={offer}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
};

export default OfferCard;
